package nl.volleyplanner.web.base.routing

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import nl.volleyplanner.web.Logger
import nl.volleyplanner.web.util.asMap
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.Location
import org.w3c.dom.events.MouseEvent
import org.w3c.dom.url.URL
import org.w3c.dom.url.URLSearchParams

data class UrlState(
    val path: String,
    val queryParameters: Map<String, String> = emptyMap(),
    val hash: String? = null
)

data class UrlSnapshot(
    val state: UrlState,
    val cause: NavigationCause
)

enum class NavigationCause {
    Initial,
    LinkClick,
    PopState,
    HashChange,
    Programmatic
}

object NavigationManager {
    private val logger = Logger.getLogger("nl.volleyplanner.web.base.routing.NavigationManager")

    private val mutableState = MutableStateFlow(
        UrlSnapshot(
            parseLocation(window.location),
            NavigationCause.Initial
        )
    )
    val state: StateFlow<UrlSnapshot> = mutableState.asStateFlow()

    /**
     * Initializes the navigation manager
     * Listens to click events to handle navigation of <a> etc.
     * Listens to popstate for going back/forward in history
     * Listens to hashchange for changes to the hash in the URL
     */
    fun initialize() {
        logger.info { "Initializing navigation manager" }
        document.addEventListener("click", { event ->
            handleClick(event as MouseEvent)
        })
        window.addEventListener("popstate", { event ->
            updateFromLocation(NavigationCause.PopState)
        })
        window.addEventListener("hashchange", { event ->
            updateFromLocation(NavigationCause.HashChange)
        })
    }

    private fun updateFromLocation(cause: NavigationCause) {
        val nextState = parseLocation(window.location)
        if (nextState == state.value.state) return

        val snapshot = UrlSnapshot(nextState, cause)
        logger.debug { "Navigated to $snapshot ($cause)" }
        mutableState.value = snapshot
    }

    fun navigateTo(url: String, cause: NavigationCause, pushState: Boolean = true) {
        val currentUrl = window.location.pathname + window.location.search + window.location.hash

        if (url != currentUrl) {
            if (pushState) {
                window.history.pushState(null, "", url)
            } else {
                window.history.replaceState(null, "", url)
            }
            updateFromLocation(cause)
        }
    }

    /**
     * Update the query parameters of the current URL
     *
     * @param parameters The query parameters to set, setting a value to null will remove the parameter
     * @param pushState If true, push the new state to browser history, otherwise replace the current history item
     */
    fun setQueryParameters(parameters: Map<String, String?>, pushState: Boolean = false) {
        val searchParams = URLSearchParams(window.location.search)

        parameters.forEach { (key, value) ->
            if (value == null) {
                searchParams.delete(key)
            }
            else {
                searchParams.set(key, value)
            }
        }

        val queryString = searchParams.toString()
        val nextUrl = buildString {
            append(window.location.pathname)
            if (queryString.isNotBlank()) {
                append("?")
                append(queryString)
            }
            append(window.location.hash)
        }
        navigateTo(nextUrl, NavigationCause.Programmatic, pushState)
    }

    /**
     * Handles a click event on the document, determines if the click was meant for navigation and
     * updates the URL if necessary
     *
     * Most common non-navigation scenarios are ignored, such as:
     *  - clicking with any other mouse button than the left mouse button
     *  - clicking with CTRL, ALT, SHIFT or META keys - these usually indicate wanting to open in a new tab
     *  - clicking on links with target="_blank", or any other target as it means
     *      the navigation is not for the current tab
     *  - And more, see the code for details as the scenarios are quite clear
     */
    fun handleClick(event: MouseEvent) {
        if (event.defaultPrevented) return
        if (event.button.toInt() != 0) return
        if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return

        val target = event.target as? HTMLElement ?: return
        val anchor = findAnchorInComposedPath(event) ?: target.closest("a[href]") as? HTMLAnchorElement ?: return

        if (anchor.target.isNotBlank()) return
        if (anchor.hasAttribute("download")) return
        if (anchor.getAttribute("rel")?.contains("external") == true) return

        if (anchor.href.isBlank()) return

        val url = URL(anchor.href)
        // Don't respond to mailto: tel: links etc.
        if (url.protocol != "http:" && url.protocol != "https:") return
        if (url.origin != window.location.origin) return

        val isSamePage = window.location.pathname == url.pathname && window.location.search == url.search
        if (isSamePage) return

        val nextUrl = url.pathname + url.search + url.hash

        event.preventDefault()
        navigateTo(nextUrl, NavigationCause.LinkClick)
    }

    private fun findAnchorInComposedPath(event: MouseEvent) : HTMLAnchorElement? {
        return event.composedPath().firstOrNull {
            it is HTMLAnchorElement && it.hasAttribute("href")
        } as? HTMLAnchorElement
    }

    private fun parseLocation(location: Location): UrlState {
        return UrlState(
            path = normalizePath(location.pathname),
            queryParameters = parseQueryParameters(location.search),
            hash = location.hash.removePrefix("#").takeIf { it.isNotBlank() }
        )
    }

    private fun parseQueryParameters(search: String): Map<String, String> {
        val params = URLSearchParams(search)
        val result = params.asMap()

        return result
    }

    private fun normalizePath(path: String): String {
        if (path.isBlank()) return "/"

        return if (path.startsWith("/")) path else "/$path"
    }
}
